import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, DatePicker, InputNumber } from "antd";
import { createBudgetForm, updateBudgetForm } from "../../actions/admin";
import { useDispatch, useSelector } from "react-redux";
import OrganizationListing from "../../components/Comman/DropdownsListing/OrganizationListing";
import DataElementsListing from "../../components/Comman/DropdownsListing/DataElementsListing";
import dayjs from "dayjs";
import { monthSelection } from "../../utils/comman";

const { Option } = Select;

interface SideDrawerProps {
  budgetData?: any; // Optional organization data
  open?: boolean;
  onClose?: any;
}

const CreateBudget: React.FC<SideDrawerProps> = ({
  budgetData,
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const dispatch: any = useDispatch();
  const loadingGlobal = useSelector((state: any) => state?.General);
  const user = useSelector((state: any) => state?.Auth?.user);
  const roleId = user?.user?.roleId;
  const [budgetDescription, setBudgetDescription] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setBudgetDescription(null);
    }
  }, [open, form]);

  // Set form values when budgetData changes or modal opens
  useEffect(() => {
    if (open) {
      if (budgetData && Object.keys(budgetData).length > 0) {
        // Editing existing budget
        const yearValue = budgetData.BudgetData_Year
          ? dayjs().year(budgetData.BudgetData_Year)
          : null;

        form.setFieldsValue({
          Organization_ID: budgetData?.Organization_ID,
          DataElements_ID: budgetData?.DataElements_ID || "",
          BudgetData_Year: yearValue,
          BudgetData_Period: budgetData?.BudgetData_Period || "",
          BudgetData_Amount: budgetData?.BudgetData_Amount || "",
        });
        setBudgetDescription(budgetData?.dataElement?.DataElements_Description);
      } else {
        // Creating new budget - set default year to current year
        form.setFieldsValue({
          Organization_ID: undefined,
          DataElements_ID: "",
          BudgetData_Year: dayjs(), // Default to current year
          BudgetData_Period: "",
          BudgetData_Amount: "",
        });
        setBudgetDescription(null);
      }
    }
  }, [budgetData, form, open]);

  const onFinish = async (values: any) => {
    // Ensure BudgetData_Year is converted to number if it's a dayjs object
    const year =
      values?.BudgetData_Year instanceof dayjs
        ? values.BudgetData_Year.year()
        : values?.BudgetData_Year;

    const payload = {
      ...values,
      BudgetData_Year: year,
    };

    if (budgetData?.id) {
      delete payload.Organization_ID;
      delete payload.DataElements_ID;
      const updatePayload = { ...payload, id: budgetData?.id };
      dispatch(
        updateBudgetForm(updatePayload, (resp: any) => {
          if (resp?.status === 200 || resp?.status === 201) {
            form.resetFields(); // Clear form on successful submission
            onClose();
          }
        })
      );
    } else {
      dispatch(
        createBudgetForm(payload, (resp: any) => {
          if (resp?.status === 200 || resp?.status === 201) {
            form.resetFields(); // Clear form on successful submission
            onClose();
          }
        })
      );
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      // Remove initialValues since we're handling this in useEffect
    >
      <div className="mb-[20px]">
        <OrganizationListing
          dataToSet={budgetData}
          pageName={budgetData?.id ? "editBudget" : "budget"}
          keyName={"Organization_ID"}
          idType="budget"
          apiType={[2, 3].includes(roleId) ? "user" : "admin"}
        />
      </div>
      <div className="mb-[20px]">
        <DataElementsListing
          dataToSet={budgetData}
          keyName={"DataElements_ID"}
          name={"budget"}
          setBudgetDescription={setBudgetDescription}
        />
      </div>
      {budgetDescription && (
        <div className="mb-[10px]">
          <Form.Item>
            <Input
              className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] px-[20px] border-1 border-transparent focus:border-white w-full placeholder-[#005A90]"
              value={budgetDescription}
              disabled
            />
          </Form.Item>
        </div>
      )}
      <div className="mb-[10px]">
        <Form.Item
          name="BudgetData_Year"
          rules={[{ required: true, message: "Please select a year" }]}
        >
          <DatePicker
            picker="year"
            placeholder="Select Year"
            disabled={[2, 3].includes(roleId)}
            style={{
              width: "100%",
              backgroundColor: "#013351",
              color: "#fff",
            }}
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[12px] px-[20px] border-1 border-transparent focus:border-white w-full placeholder-[#005A90]"
            disabledDate={(current) =>
              current && current.year() > dayjs().year()
            }
          />
        </Form.Item>
      </div>
      <div className="mb-[10px]">
        <Form.Item
          name="BudgetData_Period"
          rules={[{ required: true, message: "Please select a month" }]}
        >
          <Select
            placeholder="Select Month"
            allowClear
            style={{ width: "100%" }}
            className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[10px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto"
            // disabled={[2, 3].includes(roleId)}
          >
            <Option key="" value="">
              Budget Interval
            </Option>
            {monthSelection?.map(({ name, value }) => (
              <Option key={value} value={value}>
                {name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      {[1, 2, 3].includes(roleId) && (
        <Form.Item name="BudgetData_Amount">
          <InputNumber
            prefix={<span className="mt-[1px] inline-block text-white">$</span>}
            placeholder="Enter The Amount"
            min={0}
            step="0.01"
            controls={false}
            className="input-main-col filled-input focus:bg-transparent rounded-[5px] mb-[10px] p-[10px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#fff] text-white h-auto"
          />
        </Form.Item>
      )}
      <div className="mb-[20px]">
        <Form.Item>
          <Button
            htmlType="submit"
            className="p-[25px] text-[12px] w-full text-white rounded-md gradient-button"
            loading={loadingGlobal?.isLoading}
          >
            {budgetData && Object.keys(budgetData)?.length > 0
              ? "Update"
              : "Submit"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default CreateBudget;
